"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { api, ApiErrorCode, humanMessage, MallApiError } from "../api";
import { useSession } from "../session";
import type { Order, OrderDetail, OrderPlaced, Paged } from "../types";
import { useAsync, type Async } from "../useAsync";

/*
 * 주문 — 🔴 **회원만** (`coupons.ts` 머리말과 같은 이유로 로그인 확인 전에는 부르지 않습니다).
 *
 * 사용:
 *   const orders = useOrders(page);                 // orders.data: Paged<Order> | null
 *   const detail = useOrder(id);                    // detail.data: OrderDetail | null (id 가 null 이면 안 부름)
 *   const order = usePlaceOrder();                  // await order.place([{ productId, qty }], { recipientPhone })
 *   const cancel = useCancelOrder(orderId);         // await cancel.cancel([issueSn, …])
 */
export function useOrders(page = 1): Async<Paged<Order>> {
  const session = useSession();
  return useAsync(
    (signal) => api.get<Paged<Order>>("/orders", { query: { page }, signal }),
    `orders-${page}`,
    session.status === "member",
  );
}

/**
 * 🔴 `useAsync` 는 `id` 가 바뀌어도 새 응답이 오기 전까지 **이전 주문의 `data`** 를 들고
 *    있습니다. 목록 옆 패널처럼 id 를 갈아 끼우는 곳에서는 `data.order.id` 가 지금 id 와
 *    같은지 보고 그리세요 — 다르면 아직 옛 주문입니다.
 */
export function useOrder(id: string | number | null): Async<OrderDetail> {
  const session = useSession();
  return useAsync(
    (signal) => api.get<OrderDetail>(`/orders/${encodeURIComponent(String(id))}`, { signal }),
    `order-${id}`,
    session.status === "member" && id !== null && id !== "",
  );
}

export type CartLine = { productId: number; qty: number };

/**
 * 결제 → 발주 → 발급 (`POST /orders` 한 번 · 즉시 발급).
 *
 * 🔴 **두 번 누르면 주문이 둘입니다** — `busy` 동안 `place()` 는 아무것도 하지 않습니다.
 *    버튼도 `busy` 로 잠그세요.
 * 🔴 **성공하면 `router.replace` 로 발급 완료 화면에 갑니다.** `push` 면 완료 화면에서
 *    뒤로 가기가 결제 화면이 되어 같은 것을 한 번 더 삽니다.
 * 🔴 **9002(발급 지연)는 실패가 아닙니다** — 결제는 끝났고 발급만 늦은 것입니다.
 *    `delayed` 에 문구가 들어오면 결제 버튼을 **화면에서 없애세요**(다시 누르면 두 번 결제).
 * 🔑 `payNext` — 실 PG 가 붙으면 결제창 주소가 옵니다(지금 `fake` 는 늘 비어 있습니다).
 * 🔑 `recipientPhone` 은 `simple` 스킨의 «받는 사람 번호» 입니다. 🔴 발송 채널이 아직 없어
 *    저장만 됩니다(`docs/base/11` §5-3).
 */
export function usePlaceOrder() {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [payNext, setPayNext] = useState<{ type: string; url: string } | null>(null);
  const [delayed, setDelayed] = useState("");

  async function place(lines: CartLine[], extra: { recipientPhone?: string } = {}): Promise<void> {
    if (busy || lines.length === 0) return;

    setBusy(true);
    setError("");

    try {
      /* 🔴 요청 필드는 snake_case 입니다 (`product_id`) — `docs/base/07` §7-1. */
      const placed = await api.post<OrderPlaced>("/orders", {
        items: lines.map((line) => ({ product_id: line.productId, qty: line.qty })),
        recipient_phone: extra.recipientPhone?.trim() || undefined,
      });

      if (placed.payment?.next?.url) {
        setPayNext(placed.payment.next);
        setBusy(false);
        return;
      }

      router.replace(`/orders/${placed.order.id}/done`);
    } catch (cause) {
      if (cause instanceof MallApiError && cause.code === ApiErrorCode.UPSTREAM_UNAVAILABLE) {
        setDelayed(cause.message);
        setBusy(false);
        return;
      }
      setError(humanMessage(cause));
      setBusy(false);
    }
  }

  return { place, busy, error, payNext, delayed };
}

/**
 * 낱장 취소. 🔑 단위는 쿠폰 `id` 가 아니라 **`issueSn`**(티켓나라 발급 낱장 번호)입니다.
 * 🔴 성공하면 `router.replace` 로 주문 화면에 갑니다 — 뒤로 가기로 취소 화면에 돌아오면 안 됩니다.
 * 🔑 `setError` 는 화면의 입력 검사(«하나 이상 골라 주세요»)가 같은 자리에 문구를 내려고 씁니다.
 */
export function useCancelOrder(orderId: string) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function cancel(issueSns: number[]): Promise<void> {
    if (busy || issueSns.length === 0) return;

    setBusy(true);
    setError("");

    try {
      await api.post(`/orders/${encodeURIComponent(orderId)}/cancel`, { issue_sns: issueSns });
      router.replace(`/orders/${orderId}`);
    } catch (cause) {
      setError(humanMessage(cause));
      setBusy(false);
    }
  }

  return { cancel, busy, error, setError };
}
